import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCompany, hasCertConfigured, hasSolConfigured, updateCompany } from '../services/companyService';
import { getVerticalTheme } from '../lib/verticals';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import Badge from '../components/ui/Badge';

const EMPTY = {
  razonSocial: '',
  nombreComercial: '',
  direccion: '',
  departamento: '',
  provincia: '',
  distrito: '',
  ubigueo: '',
  telefono: '',
  serieFactura: '',
  serieBoleta: '',
  serieNotaCredito: '',
  serieNotaDebito: '',
  serieGuia: '',
  businessType: 'general',
  activo: true,
};

export default function CompanyPage() {
  const { ruc, user } = useAuth();
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const canManage = user?.rol !== 'prueba';

  const load = useCallback(async () => {
    if (!ruc) return;
    setLoading(true);
    setError(null);
    try {
      const c = await getCompany(ruc);
      setCompany(c);
      setForm({ ...EMPTY, ...c });
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [ruc]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateCompany(ruc, form);
      await load();
      setSaved(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Cargando empresa…" />;

  const theme = getVerticalTheme(form.businessType || company?.businessType);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Empresa / Configuración</h1>
          <p className="muted">
            Colección <code>company/{ruc}</code> · SIN editar SOL ni certificados desde el navegador
          </p>
        </div>
      </header>

      {error ? <div className="alert error">{error}</div> : null}
      {saved ? <div className="alert" style={{ background: '#dcfce7', color: '#166534' }}>Datos guardados.</div> : null}

      <section className="stat-grid compact">
        <div className="stat-card">
          <p className="stat-label">RUC</p>
          <p className="stat-value" style={{ fontSize: '1.2rem' }}>{ruc}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Vertical</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            <Badge tone="info">{theme?.label || 'General'}</Badge>
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">SOL</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {hasSolConfigured(company) ? <Badge tone="ok">Configurado</Badge> : <Badge tone="warn">Pendiente</Badge>}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Certificado</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {hasCertConfigured(company) ? <Badge tone="ok">Cargado</Badge> : <Badge tone="warn">Pendiente</Badge>}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Activa</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {company?.activo === false ? <Badge tone="muted">No</Badge> : <Badge tone="ok">Sí</Badge>}
          </p>
        </div>
      </section>

      <form className="form-card" onSubmit={onSubmit}>
        <h2 style={{ margin: 0, fontSize: '1rem' }}>Datos comerciales</h2>
        <div className="form-grid">
          <label className="span-2">
            Razón social
            <input value={form.razonSocial || ''} onChange={(e) => set('razonSocial', e.target.value)} disabled={!canManage} />
          </label>
          <label className="span-2">
            Nombre comercial
            <input value={form.nombreComercial || ''} onChange={(e) => set('nombreComercial', e.target.value)} disabled={!canManage} />
          </label>
          <label className="span-2">
            Dirección
            <input value={form.direccion || ''} onChange={(e) => set('direccion', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Departamento
            <input value={form.departamento || ''} onChange={(e) => set('departamento', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Provincia
            <input value={form.provincia || ''} onChange={(e) => set('provincia', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Distrito
            <input value={form.distrito || ''} onChange={(e) => set('distrito', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Ubigueo
            <input value={form.ubigueo || ''} onChange={(e) => set('ubigueo', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Teléfono
            <input value={form.telefono || ''} onChange={(e) => set('telefono', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Vertical
            <select value={form.businessType || 'general'} onChange={(e) => set('businessType', e.target.value)} disabled={!canManage}>
              <option value="general">Comercio general</option>
              <option value="ferreteria">Ferretería</option>
              <option value="farmacia">Farmacia</option>
            </select>
          </label>
        </div>

        <h2 style={{ margin: 0, fontSize: '1rem' }}>Series de comprobantes</h2>
        <p className="muted" style={{ margin: 0 }}>
          Las correlativos y la emisión SUNAT siguen en MotorNumeracion / la app. Aquí solo se
          documentan las series activas si el core las usa en <code>company</code>.
        </p>
        <div className="form-grid">
          <label>
            Factura
            <input value={form.serieFactura || ''} onChange={(e) => set('serieFactura', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Boleta
            <input value={form.serieBoleta || ''} onChange={(e) => set('serieBoleta', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Nota de crédito
            <input value={form.serieNotaCredito || ''} onChange={(e) => set('serieNotaCredito', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Nota de débito
            <input value={form.serieNotaDebito || ''} onChange={(e) => set('serieNotaDebito', e.target.value)} disabled={!canManage} />
          </label>
          <label>
            Guía
            <input value={form.serieGuia || ''} onChange={(e) => set('serieGuia', e.target.value)} disabled={!canManage} />
          </label>
        </div>

        <div className="panel" style={{ background: '#f8fafc' }}>
          <p className="muted" style={{ margin: 0 }}>
            Credenciales SOL y certificado digital se administran en la app móvil (flujo seguro).
            Este panel no los edita ni los muestra en claro.
          </p>
        </div>

        {canManage ? (
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={load}>
              Recargar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar empresa'}
            </button>
          </div>
        ) : (
          <p className="muted">Tu rol no permite editar la empresa.</p>
        )}
      </form>
    </div>
  );
}
